/**
 * Version history, shortlisting and export (SDO-2 / SDO-5), extracted whole at LAR-1.
 *
 * **Moved, not rewritten.** Everything here shipped with SDO-2 and SDO-5 and works; extracting it
 * was so the editor beside it could grow a source selector and a portfolio workflow without this
 * ending up in the same 400-line component. The behaviour and the reasons for it are unchanged:
 *
 * * Shortlist and export act on a VERSION id, never on the strategy. A flag on the mutable row
 *   would go on saying "shortlisted" while the definition underneath it changed, which is the
 *   failure the whole milestone exists to prevent.
 * * Export is offered only where shortlisting has happened, because that is the gate the route
 *   enforces — a button that is always present and usually refuses teaches people to ignore the
 *   refusal.
 * * Refusals are headlined by name (decision SDO-D2): "not validated" and "validated, then edited"
 *   are different problems with different fixes.
 */
import { Download, History, Star } from 'lucide-react';
import type { ExportRefusal, LabStrategyVersion } from '../labStrategyApi';

/**
 * What each refusal actually means, in the words the author needs.
 *
 * Decision SDO-D2 asks for one distinction by name: "not validated" and "validated, then edited"
 * are different problems with different fixes, and a single "cannot export" would send an author to
 * the wrong one. The rest are here for the same reason — the fix differs every time.
 */
const REFUSAL_HEADLINES: Record<string, string> = {
  not_validated: 'Not validated',
  validated_then_edited: 'Validated, then edited',
  not_shortlisted: 'Not shortlisted',
  not_shortlistable: 'Cannot be shortlisted',
  no_definition_hash: 'No definition hash',
  no_version_number: 'No version number',
  missing_definition: 'No stored definition',
  unsupported_source_mode: 'Nothing to deploy',
  unresolved_dependencies: 'Sleeves unresolved',
  superseded_portfolio: 'Superseded',
};

export function statusTone(status: string | null): string {
  if (status === 'valid') return 'bg-emerald-50 text-emerald-800';
  if (status === 'invalid') return 'bg-red-50 text-red-700';
  // `stale` and `pending` share a tone deliberately: both mean "do not trust this yet", and
  // distinguishing them visually would imply a difference the author cannot act on differently.
  return 'bg-amber-50 text-amber-800';
}

interface Props {
  versions: LabStrategyVersion[];
  refusal: ExportRefusal | null;
  busy: boolean;
  onToggleShortlist: (version: LabStrategyVersion) => void;
  onExport: (version: LabStrategyVersion) => void;
}

export function VersionHistory({ versions, refusal, busy, onToggleShortlist, onExport }: Props) {
  return (
    <div>
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <History className="h-4 w-4" aria-hidden="true" /> Version history
      </h2>
      <ul className="mt-2 space-y-2 text-xs">
        {versions.map((version) => (
          <li key={version.id} className="flex flex-wrap items-center gap-2 text-slate-600">
            {/* The ordinal, which SDO-2 added precisely so identity is not a hash prefix or a
                position in a list — a number that changes when history is pruned is not an
                identity. The hash still shows, because it is what the far side compares against. */}
            <span className="font-sans font-semibold text-slate-900">
              v{version.version_number ?? '—'}
            </span>
            <span className="font-mono">
              {version.definition_hash?.slice(7, 19) ?? version.version_hash.slice(0, 12)}
            </span>
            {version.is_current && (
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-emerald-800">
                CURRENT
              </span>
            )}
            <span
              className={`rounded px-1.5 py-0.5 font-sans text-[10px] font-semibold ${statusTone(version.validation_status)}`}
            >
              {version.validation_status ?? 'unvalidated'}
            </span>
            <span className="text-slate-400">{version.created_at?.slice(0, 19)}</span>

            <button
              type="button"
              disabled={busy}
              onClick={() => onToggleShortlist(version)}
              className={`rounded px-2 py-0.5 font-sans text-[10px] font-semibold ${
                version.is_shortlisted
                  ? 'bg-amber-100 text-amber-900 hover:bg-amber-200'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              } disabled:opacity-50`}
            >
              <Star className="mr-1 inline h-3 w-3" aria-hidden="true" />
              {version.is_shortlisted ? 'Shortlisted' : 'Shortlist'}
            </button>

            {/* Offered only on a shortlisted version, because that is the gate the route enforces.
                A button that is always present and usually refuses teaches people to ignore the
                refusal. */}
            {version.is_shortlisted && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onExport(version)}
                className="rounded bg-emerald-700 px-2 py-0.5 font-sans text-[10px] font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                <Download className="mr-1 inline h-3 w-3" aria-hidden="true" />
                Export
              </button>
            )}
          </li>
        ))}
      </ul>

      {refusal && (
        <p role="alert" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span className="font-semibold">{REFUSAL_HEADLINES[refusal.reason] ?? 'Cannot export'}</span>
          {' — '}
          {refusal.message}
        </p>
      )}

      <p className="mt-3 text-xs text-slate-500">
        Shortlist an exact version to mark it as a deployment candidate, then export it as a package
        file. Import it in PortfoliYo Trade — there is no link between the two products, which is
        what keeps either one working while the other is down.
      </p>
    </div>
  );
}
